import React, { useState, useEffect } from 'react';
import PaidInfo from './PaidInfo';
import Pagination from './Pagination';
import './CUPLTable.css';

const CUPLTable = ({ 
  cupls, 
  onEdit, 
  onMarkPaid, 
  onReject, 
  onDelete, 
  userRole, 
  balance, 
  onPreviewPDF, 
  onPagoRunt, 
  onViewReceipt, 
  processingRuntId,
  selectedCuplIds = [],
  onSelectCupl,
  onSelectAll
}) => {
  // Estado para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Resetear página cuando cambien los datos (filtros externos)
  useEffect(() => {
    setCurrentPage(1);
  }, [cupls.length]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO') + ' ' + date.toLocaleTimeString('es-CO');
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    // Ajustar por zona horaria local para evitar que cambie de día
    const localDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return localDate.toLocaleDateString('es-CO');
  };

  const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expDate = new Date(expirationDate);
    // Ajustar por zona horaria local
    const localExpDate = new Date(expDate.getTime() + expDate.getTimezoneOffset() * 60000);
    localExpDate.setHours(0, 0, 0, 0);
    return localExpDate < today;
  };

  const formatCUPLDisplay = (cuplNumber) => {
    if (!cuplNumber) return '';
    const prefix = '600000000';
    if (cuplNumber.startsWith(prefix)) {
      return (
        <span title={cuplNumber}>
          <small style={{ color: '#94a3b8', fontSize: '0.8em', marginRight: '2px' }}>600...</small>
          {cuplNumber.substring(prefix.length)}
        </span>
      );
    }
    return cuplNumber;
  };

  const handleCopyCUPL = (cupl_number) => {
    // Intentar usar Clipboard API moderno primero
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(cupl_number)
        .then(() => {
          const event = new Event('copyed');
          window.dispatchEvent(event);
        })
        .catch((err) => {
          console.error('Error al copiar al portapapeles:', err);
          // Fallback a método antiguo
          copyToClipboardFallback(cupl_number);
        });
    } else {
      // Fallback para navegadores más antiguos o contextos HTTP
      copyToClipboardFallback(cupl_number);
    }
  };

  const copyToClipboardFallback = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      const event = new Event('copyed');
      window.dispatchEvent(event);
    } catch (err) {
      console.error('Error al copiar con fallback:', err);
    }
    document.body.removeChild(textArea);
  };

  // Solo superadmin puede editar y eliminar
  const canEdit = userRole === 'superadmin';
  const canDelete = userRole === 'superadmin';
  // Todos los usuarios auténticos pueden marcar como pagado
  const canMarkPaid = userRole === 'user' || userRole === 'admin' || userRole === 'superadmin';

  // Verificar si hay saldo suficiente para marcar como pagado
  const canMarkAsPaidByBalance = (cupl) => {
    // Si el CUPL ya está pagado, no mostrar el botón
    if (cupl.is_paid) {
      return false;
    }

    // Si la fecha de vencimiento ya pasó, no permitir pagar
    if (isExpired(cupl.expiration_date)) {
      return false;
    }
    
    // Si no hay balance definido, no permitir pagar
    if (balance === null || balance === undefined) {
      console.log(`CUPL ${cupl.cupl_number}: Balance es null/undefined`, balance);
      return false;
    }
    
    // Obtener el valor real a pagar (priorizar total_amount sobre tramite_value)
    const realValue = cupl.total_amount || cupl.tramite_value;
    
    // Verificar si hay valor de trámite o monto total
    if (!realValue && realValue !== 0) {
      console.log(`CUPL ${cupl.cupl_number}: Sin tramite_value ni total_amount`);
      return false;
    }

    try {
      // Convertir a números para comparación segura
      const numBalance = Number(balance);
      const numRealValue = Number(realValue);

      // Si alguno no es válido, no permitir pagar
      if (isNaN(numBalance) || isNaN(numRealValue) || numBalance < 0 || numRealValue <= 0) {
        console.log(`CUPL ${cupl.cupl_number}: Valores inválidos - Balance=${numBalance}, Requerido=${numRealValue}`);
        return false;
      }

      const puedePagar = numBalance >= numRealValue;
      console.log(`✓ CUPL ${cupl.cupl_number}: Balance=${numBalance}, Requerido=${numRealValue}, Permite=${puedePagar}`);

      return puedePagar;
    } catch (e) {
      console.error(`Error en validación de CUPL ${cupl.cupl_number}:`, e);
      return false;
    }
  };

  // No permitir editar o eliminar si está pagado
  const canEditThisCupl = (cupl) => canEdit && !cupl.is_paid;
  const canDeleteThisCupl = (cupl) => canDelete && !cupl.is_paid;

  // Lógica de paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = cupls.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const isAllSelected = currentItems.length > 0 && currentItems.every(c => selectedCuplIds.includes(c.id));

  return (
    <div className="table-wrapper">
      <div className="table-summary">
        <p>Mostrando <strong>{currentItems.length}</strong> de <strong>{cupls.length}</strong> registro{cupls.length !== 1 ? 's' : ''}</p>
      </div>
      <table className="cupl-table">
        <thead>
          <tr>
            <th className="checkbox-cell">
              <input 
                type="checkbox" 
                checked={isAllSelected}
                onChange={(e) => onSelectAll(currentItems, e.target.checked)}
              />
            </th>
            <th>CUPL</th>
            <th>Fecha Creación</th>
            <th>Vencimiento</th>
            <th>Placa</th>
            <th>Propietario</th>
            <th>Tipo de Trámite</th>
            <th>Creado por</th>
            <th>Estado</th>
            <th>Info Pago</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map(cupl => (
            <tr key={cupl.id} className={`${cupl.is_paid ? 'paid' : cupl.is_rejected ? 'rejected-row' : 'pending'} ${isExpired(cupl.expiration_date) && !cupl.is_paid ? 'expired-row' : ''}`}>
              <td data-label="Seleccionar" className="checkbox-cell">
                <input 
                  type="checkbox" 
                  checked={selectedCuplIds.includes(cupl.id)}
                  onChange={() => onSelectCupl(cupl.id)}
                />
              </td>
              <td data-label="CUPL">
                <div className="cupl-cell">
                  <span className="cupl-number-text">{formatCUPLDisplay(cupl.cupl_number)}</span>
                  <button
                    onClick={() => handleCopyCUPL(cupl.cupl_number)}
                    className="copy-btn"
                    title={`Copiar CUPL completo: ${cupl.cupl_number}`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  </button>
                </div>
              </td>
              <td data-label="Fecha Creación" className="date">{formatDate(cupl.created_at)}</td>
              <td data-label="Vencimiento" className={`expiration-date ${isExpired(cupl.expiration_date) && !cupl.is_paid ? 'expired' : ''}`}>
                {cupl.expiration_date ? (
                  <div className="expiration-cell">
                    <span>{formatDateOnly(cupl.expiration_date)}</span>
                    {isExpired(cupl.expiration_date) && !cupl.is_paid && (
                      <span className="expired-badge" title="La factura ha vencido">⚠️ VENCIDA</span>
                    )}
                  </div>
                ) : (
                  <span className="no-info">-</span>
                )}
              </td>
              <td data-label="Placa" className="plate">{cupl.plate}</td>
              <td data-label="Propietario">{cupl.owner_name}</td>
              <td data-label="Tipo de Trámite" className="tramite-type">
                {cupl.tramite_name ? (
                  <div>
                    <strong>{cupl.tramite_name}</strong>
                    <br />
                    <small>${(cupl.total_amount || cupl.tramite_value)?.toLocaleString('es-CO')}</small>
                  </div>
                ) : (
                  <span className="no-info">
                    {cupl.total_amount ? `$${Number(cupl.total_amount).toLocaleString('es-CO')}` : '-'}
                  </span>
                )}
              </td>
              <td data-label="Creado por" className="created-by">{cupl.created_by_user || '-'}</td>
              <td data-label="Estado">
                {cupl.is_rejected ? (
                  <span className="status rejected" title={cupl.rejection_reason}>
                    ✕ Rechazado
                  </span>
                ) : (
                  <span className={`status ${cupl.is_paid ? 'paid' : 'pending'}`}>
                    {cupl.is_paid ? '✓ Pagado' : '⏳ Pendiente'}
                  </span>
                )}
              </td>
              <td data-label="Info Pago" className="paid-info">
                {cupl.is_paid ? (
                  <PaidInfo paidBy={cupl.paid_by_user} paidAt={cupl.paid_at} />
                ) : (
                  <span className="no-info">-</span>
                )}
              </td>
              <td data-label="Acciones" className="actions-cell">
                <div className="actions-container">
                  <button
                    onClick={() => onPreviewPDF(cupl)}
                    className="table-action-btn btn-preview"
                    title="Ver PDF"
                  >
                    <span className="icon-symbol">👁️</span>
                  </button>
                  {cupl.is_paid && (
                    <button
                      onClick={() => onViewReceipt(cupl)}
                      className="table-action-btn btn-receipt"
                      title="Ver Recibo de Pago"
                      style={{ backgroundColor: '#7c3aed' }}
                    >
                      <span className="icon-symbol">☷</span>
                    </button>
                  )}
                  {canMarkPaid && !cupl.is_paid && !cupl.is_rejected && (
                    <button
                      onClick={() => onPagoRunt(cupl)}
                      className={`table-action-btn btn-runt ${processingRuntId === cupl.id ? 'loading' : ''}`}
                      title={
                        isExpired(cupl.expiration_date) 
                          ? 'No se puede pagar un CUPL vencido' 
                          : processingRuntId === cupl.id 
                            ? "Procesando pago..." 
                            : "Pago Automático RUNT"
                      }
                      disabled={processingRuntId !== null || isExpired(cupl.expiration_date)}
                    >
                      {processingRuntId === cupl.id ? (
                        <div className="spinner-mini"></div>
                      ) : (
                        <span className="icon-symbol" style={{ fontWeight: 'bold' }}>$</span>
                      )}
                    </button>
                  )}
                  {canEditThisCupl(cupl) && (
                    <button
                      onClick={() => onEdit(cupl)}
                      className="table-action-btn btn-edit"
                      title="Editar registro"
                    >
                      <span className="icon-symbol">✎</span>
                    </button>
                  )}
                  {canMarkPaid && !cupl.is_paid && !cupl.is_rejected && (
                    <button
                      onClick={() => onMarkPaid(cupl.id)}
                      className="table-action-btn btn-paid"
                      title={
                        isExpired(cupl.expiration_date) 
                          ? 'Factura vencida' 
                          : !canMarkAsPaidByBalance(cupl) 
                            ? `Saldo insuficiente ($${cupl.tramite_value?.toLocaleString('es-CO')})` 
                            : 'Marcar como pagado'
                      }
                      disabled={!canMarkAsPaidByBalance(cupl) || isExpired(cupl.expiration_date)}
                    >
                      <span className="icon-symbol">✓</span>
                    </button>
                  )}
                  {canMarkPaid && !cupl.is_paid && !cupl.is_rejected && isExpired(cupl.expiration_date) && (
                    <button
                      onClick={() => onReject(cupl)}
                      className="table-action-btn btn-reject"
                      title="Marcar como rechazado"
                    >
                      <span className="icon-symbol">✕</span>
                    </button>
                  )}
                  {canDeleteThisCupl(cupl) && (
                    <button
                      onClick={() => onDelete(cupl)}
                      className="table-action-btn btn-delete"
                      title="Eliminar registro"
                    >
                      <span className="icon-symbol">🗑</span>
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      <Pagination 
        currentPage={currentPage}
        totalItems={cupls.length}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
      
      {/* Mostrar leyenda de permisos */}
      <div className="permissions-note">
        <small>
          {userRole === 'superadmin' && '🔐 Tienes permisos completos (editar, eliminar, marcar como pagado)'}
          {userRole === 'admin' && '👨‍💼 Puedes marcar como pagado'}
          {userRole === 'user' && '👤 Puedes registrar y marcar como pagado'}
        </small>
      </div>
    </div>
  );
};

export default CUPLTable;
